'use client';

import { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';

import { getUsersReport, downloadUsersCSV } from 'features/admin/reportApi';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUsers,
  faUserTie,
  faStoreAlt,
  faToggleOn,
  faToggleOff,
  faTimes,
  faSpinner,
  faFileExport,
  faDownload,
  faEnvelope,
  faPhone,
  faLocationDot,
  faGraduationCap,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: number;
  created_at: string;
  deleted_at?: string;
  phone?: string;
  qualifications?: string;
  experience_years?: number;
  location?: string;
  locum_role?: string;
  pharmacy_name?: string;
  contact_person?: string;
  pharmacy_location?: string;
  license_number?: string;
}

const UserReportPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState({
    total: 0,
    locums: 0,
    pharmacies: 0,
    active: 0,
    inactive: 0
  });

  // FETCH REPORTS
  const fetchReport = async (pageNumber = 1) => {
    try {
      setLoading(true);
      setError('');

      const filters: any = {
        page: pageNumber,
        limit: 10
      };

      if (search) filters.search = search;
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter) filters.status = statusFilter;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const data = await getUsersReport(filters);

      setUsers(data.users || []);
      setPage(data.page || 1);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // Calculate stats
      const userData = data.users || [];
      setStats({
        total: data.total || 0,
        locums: userData.filter((u: any) => u.role === 'locum').length,
        pharmacies: userData.filter((u: any) => u.role === 'pharmacy').length,
        active: userData.filter((u: any) => u.is_active).length,
        inactive: userData.filter((u: any) => !u.is_active).length
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch reports');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Calculate default date range (last 7 days)
    const today = new Date();
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    
    const defaultStart = formatDate(lastWeek);
    const defaultEnd = formatDate(today);
    
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    
    // Fetch report with default dates
    const filters: any = {
      page: 1,
      limit: 10,
      startDate: defaultStart,
      endDate: defaultEnd
    };
    
    getUsersReport(filters)
      .then(data => {
        setUsers(data.users || []);
        setPage(data.page || 1);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        
        const userData = data.users || [];
        setStats({
          total: data.total || 0,
          locums: userData.filter((u: any) => u.role === 'locum').length,
          pharmacies: userData.filter((u: any) => u.role === 'pharmacy').length,
          active: userData.filter((u: any) => u.is_active).length,
          inactive: userData.filter((u: any) => !u.is_active).length
        });
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err.message || 'Failed to fetch reports');
        console.error('Fetch error:', err);
        setLoading(false);
      });
  }, []);

  // SEARCH
  const handleSearch = () => {
    fetchReport(1);
  };

  // APPLY FILTERS
  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === 'role') setRoleFilter(value);
    if (filterType === 'status') setStatusFilter(value);
    setPage(1);
  };

  // HANDLE DATE CHANGE
  const handleDateChange = (dateType: string, value: string) => {
    if (dateType === 'startDate') {
      setStartDate(value);
      // Auto set end date to start date if end date is not set
      if (!endDate) {
        setEndDate(value);
      }
      // Reset end date if it's before the new start date
      if (endDate && value > endDate) {
        setEndDate(value);
      }
    } else if (dateType === 'endDate') {
      // Validate that end date is not less than start date
      if (startDate && value < startDate) {
        setError('End date cannot be less than start date');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setEndDate(value);
    }
    setPage(1);
  };

  // EXPORT CSV
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const filters: any = {};

      if (search) filters.search = search;
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter) filters.status = statusFilter;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      await downloadUsersCSV(filters);
      setSuccess('Report exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to export report');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container className="py-2 py-lg-4" fluid>
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                Admin Analytics
              </p>
              <h2 className="text-body-emphasis mb-1">User Report</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                View comprehensive user statistics, profile details, and export
                data.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={2} lg={2.4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Total Users
                  </p>
                  <h3 className="text-body-emphasis mb-0">{stats.total}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faUsers} className="text-primary" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Locums
                  </p>
                  <h3 className="text-info mb-0">{stats.locums}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faUserTie} className="text-info" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Pharmacies
                  </p>
                  <h3 className="text-success mb-0">{stats.pharmacies}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faStoreAlt} className="text-success" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Active
                  </p>
                  <h3 className="text-success mb-0">{stats.active}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faToggleOn} className="text-success" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={2} lg={2.4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Inactive
                  </p>
                  <h3 className="text-danger mb-0">{stats.inactive}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faToggleOff} className="text-danger" />
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert
              variant="danger"
              className="mb-0"
              onClose={() => setError('')}
              dismissible
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Success Alert */}
      {success && (
        <Row className="mb-4">
          <Col>
            <Alert
              variant="success"
              className="mb-0"
              onClose={() => setSuccess('')}
              dismissible
            >
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filters and Search */}
      <Row className="mb-4 g-2">
        <Col lg={4}>
          <label className="form-label fs-9 fw-semibold text-body-secondary mb-2">
            Search
          </label>
          <SearchBox
            placeholder="Search by name, email, or location..."
            className="w-100"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
        </Col>

        <Col lg={2}>
          <label className="form-label fs-9 fw-semibold text-body-secondary mb-2">
            Role
          </label>
          <select
            className="form-select"
            value={roleFilter}
            onChange={e => handleFilterChange('role', e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="locum">Locum</option>
            <option value="pharmacy">Pharmacy</option>
          </select>
        </Col>

        <Col lg={2}>
          <label className="form-label fs-9 fw-semibold text-body-secondary mb-2">
            Status
          </label>
          <select
            className="form-select"
            value={statusFilter}
            onChange={e => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </Col>

        <Col lg={2}>
          <label className="form-label fs-9 fw-semibold text-body-secondary mb-2">
            Start Date
          </label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={e => handleDateChange('startDate', e.target.value)}
            placeholder="Start Date"
          />
        </Col>

        <Col lg={2}>
          <label className="form-label fs-9 fw-semibold text-body-secondary mb-2">
            End Date
          </label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={e => handleDateChange('endDate', e.target.value)}
            placeholder="End Date"
          />
        </Col>
      </Row>
      <Row className="mb-4">
        <Col lg={2} className="d-flex gap-2">
          <Button
            variant="phoenix-primary"
            className="flex-grow-1"
            onClick={handleSearch}
            startIcon={<FontAwesomeIcon icon={faSearch} />}
          >
            Search
          </Button>
        </Col>
        <Col lg={2}>
          <Button
            variant="phoenix-primary"
            onClick={handleExportCSV}
            disabled={exportLoading || users.length === 0}
            startIcon={
              exportLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faDownload} />
              )
            }
          >
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Col>
      </Row>

      {/* Loading State */}
      {loading && (
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary fw-semibold">
              Loading user report...
            </p>
          </Col>
        </Row>
      )}

      {/* Table Section */}
      {!loading && (
        <Row>
          <Col>
            <div className="card border-0 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table align-middle mb-0 fs-8">
                  <thead className="bg-body-secondary">
                    <tr>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-3 py-3 border-0">
                        User
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-3 pe-4 py-3 border-0 text-center">
                        Role
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0">
                        Details
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Status
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Joined
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-6 border-0">
                          <div className="mb-3">
                            <FontAwesomeIcon
                              icon={faUsers}
                              className="text-body-tertiary"
                              size="2x"
                            />
                          </div>
                          <p className="text-body-tertiary fw-semibold mb-0">
                            No users found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      users.map(user => (
                        <tr
                          key={user.id}
                          className="border-bottom border-translucent hover-row"
                        >
                          <td className="ps-4 pe-3 py-3 border-0">
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-m flex-shrink-0">
                                <span className="avatar-initials rounded-circle bg-primary-subtle">
                                  <FontAwesomeIcon
                                    icon={
                                      user.role === 'locum'
                                        ? faUserTie
                                        : faStoreAlt
                                    }
                                    className={
                                      user.role === 'locum'
                                        ? 'text-info'
                                        : 'text-success'
                                    }
                                  />
                                </span>
                              </div>
                              <div className="flex-grow-1 min-width-0">
                                <p className="mb-0 fw-semibold text-body-emphasis text-truncate">
                                  {user.name}
                                </p>
                                <p className="mb-0 text-body-tertiary fs-10 text-truncate">
                                  <FontAwesomeIcon
                                    icon={faEnvelope}
                                    className="me-1"
                                  />
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="ps-3 pe-4 py-3 border-0 text-center">
                            <Badge
                              bg={user.role === 'locum' ? 'info' : 'success'}
                              className="rounded-pill fw-semibold"
                            >
                              {user.role === 'locum' ? 'Locum' : 'Pharmacy'}
                            </Badge>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0">
                            {user.role === 'locum' ? (
                              <div className="fs-9">
                                {user.qualifications && (
                                  <p className="mb-1">
                                    <FontAwesomeIcon
                                      icon={faGraduationCap}
                                      className="me-2 text-primary"
                                    />
                                    {user.qualifications}
                                  </p>
                                )}
                                {user.locum_role && (
                                  <p className="mb-1">
                                    <FontAwesomeIcon
                                      icon={faBriefcase}
                                      className="me-2 text-success"
                                    />
                                    {user.locum_role}
                                  </p>
                                )}
                                {user.location && (
                                  <p className="mb-0">
                                    <FontAwesomeIcon
                                      icon={faLocationDot}
                                      className="me-2 text-danger"
                                    />
                                    {user.location}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="fs-9">
                                {user.pharmacy_name && (
                                  <p className="mb-1">
                                    <FontAwesomeIcon
                                      icon={faStoreAlt}
                                      className="me-2 text-success"
                                    />
                                    {user.pharmacy_name}
                                  </p>
                                )}
                                {user.contact_person && (
                                  <p className="mb-1 text-body-tertiary">
                                    {user.contact_person}
                                  </p>
                                )}
                                {user.pharmacy_location && (
                                  <p className="mb-0">
                                    <FontAwesomeIcon
                                      icon={faLocationDot}
                                      className="me-2 text-danger"
                                    />
                                    {user.pharmacy_location}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <div className="d-flex flex-column align-items-center gap-1">
                              <Badge
                                bg={user.is_active ? 'success' : 'danger'}
                                className="rounded-pill fw-semibold"
                              >
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>

                              {user.deleted_at && (
                                <Badge
                                  bg="secondary"
                                  className="rounded-pill fw-semibold"
                                >
                                  <span className="fs-10">
                                    Deleted: {user.deleted_at}
                                  </span>
                                </Badge>
                              )}
                            </div>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <small className="text-body-tertiary fw-semibold">
                             {user.created_at}
                            </small>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && users.length > 0 && (
                <div className="card-footer bg-body border-top border-translucent py-3">
                  <ul className="pagination justify-content-center mb-0 gap-1">
                    {page > 1 && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchReport(page - 1)}
                        >
                          Previous
                        </button>
                      </li>
                    )}

                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          page === i + 1 ? 'active' : ''
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => fetchReport(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {page < totalPages && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchReport(page + 1)}
                        >
                          Next
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      <style>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s ease;
        }
        .min-width-0 {
          min-width: 0;
        }
      `}</style>
    </Container>
  );
};

export default UserReportPage;
